import React, { useState, useEffect } from 'react';
import { Redirect } from 'react-router-dom';
import axios from 'axios';

import useInput from '../../hooks/useInput';
import ResetPasswordValidator from '../../helpers/validators/reset-password.validator';
import TextInput from '../common/InputWithLabel';
import Error from '../common/Error';
import SubmitButton from '../common/PrimaryButton';
import findErrorField from '../../helpers/serverError';

import './ResetPasswordForm.module.css';

const ResetPasswordForm = React.memo(({ token }) => {
  const [expired, setExpired] = useState(false);
  const [reset, setReset] = useState(false);
  const [emailAddress, setEmailAddress] = useState('');
  const [errors, setErrors] = useState({ password: '', confirmPassword: '' });
  const password = useInput('');
  const confirmPassword = useInput('');
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    const handleSuccess = ({ data }) => {
      if (data.Valid) {
        setEmailAddress(data.EmailAddress);
      } else {
        setExpired(true);
      }
    };

    const handleError = () => {
      setExpired(true);
    };

    axios
      .get(
        `${process.env.REACT_APP_BASE_URL}account/reset-password/validate?token=${token}`,
      )
      .then(handleSuccess)
      .catch(handleError);
  }, [token]);

  const onSubmit = event => {
    event.preventDefault();

    const handleSuccess = ({ data }) => {
      if (data.Valid) {
        setReset(true);
      }
    };

    const handleError = ({ response }) => {
      if (response && response.data && response.data.ValidationErrors) {
        const passwordError = findErrorField(
          response.data.ValidationErrors,
          'Password',
        );

        const confirmPasswordError = findErrorField(
          response.data.ValidationErrors,
          'PasswordConfirm',
        );

        setErrors({
          password: passwordError && passwordError.Messages[0],
          confirmPassword:
            confirmPasswordError && confirmPasswordError.Messages[0],
        });
      } else {
        setServerError('Something went wrong. Please try again.');
      }
    };

    const validation = ResetPasswordValidator.validate({
      password: password.value,
      confirmPassword: confirmPassword.value,
    });

    setErrors({
      password: validation.password.message,
      confirmPassword: validation.confirmPassword.message,
    });

    setServerError('');

    if (validation.isValid) {
      axios
        .post(`${process.env.REACT_APP_BASE_URL}account/change-password`, {
          EmailAddress: emailAddress,
          Password: password.value,
          PasswordConfirm: confirmPassword.value,
        })
        .then(handleSuccess)
        .catch(handleError);
    }
  };

  return (
    <>
      {expired && <Redirect to="/login/reset-password/expired" />}
      {reset && <Redirect to="/login/reset-password/confirm" />}
      <form onSubmit={onSubmit}>
        <h3>Password must:</h3>
        <ul>
          <li>Be at least 10 characters long</li>
          <li>
            Contain capital letter, lowercase letter, numeric and special
            character.
          </li>
          <li>Have NO spaces</li>
          {/* eslint-disable-next-line react/no-unescaped-entities */}
          <li>Have NO '\' or '/' characters</li>
        </ul>
        <TextInput
          type="password"
          name="password"
          label="Enter New Password"
          autocomplete="off"
          error={errors.password}
          {...password}
        />
        <TextInput
          type="password"
          name="confirmPassword"
          label="Confirm Password"
          autocomplete="off"
          error={errors.confirmPassword}
          {...confirmPassword}
        />
        {serverError && <Error error={serverError} />}
        <SubmitButton text="Submit" />
      </form>
    </>
  );
});

export default ResetPasswordForm;
